import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  ProgressBar,
  SearchBox,
  Select,
  Spinner,
  Switch,
  Tab,
  TabList,
  Textarea,
  Checkbox,
} from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowRight16Regular,
  Delete20Regular,
  Dismiss24Regular,
  Flag16Filled,
  Flash20Regular,
  Headset20Regular,
  Library20Regular,
  Mail20Regular,
  PersonCircle20Regular,
  PlugConnected20Regular,
  Play20Regular,
  Rocket20Regular,
  Settings20Regular,
  Stop20Regular,
  Timer20Regular,
  Trophy20Regular,
  DataBarVertical20Regular,
  Copy20Regular,
  ArrowDownload20Regular,
  Checkmark20Regular,
} from "@fluentui/react-icons";
import styles from "./TaskManager.module.css";
import px from "./Taskpages.module.css";
import { R, api, crud, iso, msgOf, parseISO, prioFromApi, utc } from "./Api/Taskapi";
import type { Me, Person, Priority, Project } from "./Api/Taskapi";

/* ------------------------------------------------------------------ *
 *  The other pages of the Task Manager. Each one reads and writes the
 *  Task Manager API through taskApi.ts; the white theme and the
 *  existing TaskManager.module.css classes are reused, anything new is
 *  in TaskPages.module.css.
 * ------------------------------------------------------------------ */

export type Notify = (intent: "success" | "error" | "info", text: string) => void;

export interface PageCtx {
  me: Me;
  workspaceId: number;
  projects: Project[];
  projectId: number | null;
  people: Person[];
  notify: Notify;
  openTask: (id: number) => void;
  reloadProjects: () => void;
  refreshUnread: () => void;
}

/* ---------- helpers ---------- */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const pad = (n: number) => String(n).padStart(2, "0");
const fmtDate = (s?: string | null) => {
  if (!s) return "—";
  const d = parseISO(s);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
const fmtStamp = (s?: string | null) => {
  if (!s) return "—";
  const d = utc(s);
  return `${pad(d.getDate())} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtMin = (m: number) => `${Math.floor(m / 60)}h ${pad(Math.round(m % 60))}m`;
const asList = <T,>(x: unknown): T[] => (Array.isArray(x) ? (x as T[]) : x && typeof x === "object" && Array.isArray((x as { items?: unknown }).items) ? ((x as { items: T[] }).items) : []);
const nice = (s: string) => s.toLowerCase().replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const mondayOf = (d: Date) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const chipTone = (s: string) => {
  const v = s.toUpperCase();
  if (/(DONE|COMPLETE|RESOLVED|APPROVED|ACHIEVED|ON_TRACK|ACTIVE|OK|CLOSED)/.test(v)) return px.chipGreen;
  if (/(RISK|WARN|SUBMITTED|PENDING|PLANNED|OPEN|NEW|MEDIUM|IN_PROGRESS|PAUSED)/.test(v)) return px.chipAmber;
  if (/(OFF_TRACK|BREACH|REJECT|CRITICAL|HIGH|FAIL|ERROR|URGENT)/.test(v)) return px.chipRed;
  return px.chipGrey;
};
const Chip: React.FC<{ text: string }> = ({ text }) => <span className={`${px.chip} ${chipTone(text)}`}>{nice(text)}</span>;

const Prio: React.FC<{ p: Priority }> = ({ p }) => (
  <span className={`${styles.prio} ${styles["prio" + p]}`}>
    <Flag16Filled /> {p}
  </span>
);

function useLoad<T>(fn: () => Promise<T>, deps: React.DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let live = true;
    setLoading(true);
    setError("");
    fnRef
      .current()
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(msgOf(e)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);
  return { data, loading, error, reload: useCallback(() => setTick((t) => t + 1), []), setData };
}

const Head: React.FC<{ icon: React.ReactNode; title: string; sub: string; children?: React.ReactNode }> = ({ icon, title, sub, children }) => (
  <div className={styles.head}>
    <div className={styles.headLeft}>
      <span className={styles.logo}>{icon}</span>
      <div>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.sub}>{sub}</p>
      </div>
    </div>
    {children ? <div className={styles.headRight}>{children}</div> : null}
  </div>
);

const Page: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.body}>
    <div className={px.pageBody}>{children}</div>
  </div>
);

const Wait: React.FC<{ loading: boolean; error: string; empty?: string | false; onRetry?: () => void }> = ({ loading, error, empty, onRetry }) => {
  if (loading) return <Spinner size="small" label="Loading…" className={px.wait} />;
  if (error)
    return (
      <MessageBar intent="error" className={px.inlineBar}>
        <MessageBarBody>
          {error}{" "}
          {onRetry && (
            <Button size="small" appearance="transparent" onClick={onRetry}>
              Try again
            </Button>
          )}
        </MessageBarBody>
      </MessageBar>
    );
  if (empty) return <div className={styles.emptyRow}>{empty}</div>;
  return null;
};

const Tile: React.FC<{ n: React.ReactNode; label: string }> = ({ n, label }) => (
  <div className={px.tile}>
    <div className={px.tileNum}>{n}</div>
    <div className={px.tileLbl}>{label}</div>
  </div>
);

const FormDialog: React.FC<{
  open: boolean;
  title: string;
  action: string;
  busy?: boolean;
  disabled?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
}> = ({ open, title, action, busy, disabled, onClose, onSubmit, children }) => (
  <Dialog open={open} onOpenChange={(_, o) => !o.open && onClose()}>
    <DialogSurface className={styles.dialog}>
      <DialogBody>
        <DialogTitle
          action={
            <DialogTrigger action="close">
              <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
            </DialogTrigger>
          }
        >
          {title}
        </DialogTitle>
        <DialogContent className={styles.formBody}>{children}</DialogContent>
        <DialogActions>
          <DialogTrigger disableButtonEnhancement>
            <Button appearance="secondary">Cancel</Button>
          </DialogTrigger>
          <Button className={styles.primary} appearance="primary" disabled={!!disabled || !!busy} onClick={onSubmit}>
            {busy ? "Working…" : action}
          </Button>
        </DialogActions>
      </DialogBody>
    </DialogSurface>
  </Dialog>
);

/** small helper: run an action, tell the person how it went */
const useAct = (notify: Notify) =>
  useCallback(
    async (fn: () => Promise<unknown>, ok?: string) => {
      try {
        await fn();
        if (ok) notify("success", ok);
        return true;
      } catch (e) {
        notify("error", msgOf(e));
        return false;
      }
    },
    [notify],
  );

const downloadCsv = (name: string, columns: string[], rows: unknown[][]) => {
  const cell = (v: unknown) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const text = [columns, ...rows].map((r) => r.map(cell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};

/* =================================================================== *
 *  Inbox
 * =================================================================== */
interface Notif {
  id: number;
  type: string;
  title: string;
  body?: string | null;
  resourceType?: string | null;
  resourceId?: number | null;
  isRead: boolean;
  createdAt: string;
}

export const InboxPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const act = useAct(ctx.notify);
  const l = useLoad(async () => asList<Notif>(await api("GET", "/api/notifications/inbox", undefined, { unreadOnly, pageSize: 100 })), [unreadOnly]);
  const items = l.data ?? [];

  const after = () => {
    l.reload();
    ctx.refreshUnread();
  };
  const open = async (n: Notif) => {
    if (!n.isRead) await act(() => api("POST", `/api/notifications/${n.id}/read`));
    after();
    if (n.resourceType?.toUpperCase() === "TASK" && n.resourceId) ctx.openTask(n.resourceId);
  };

  return (
    <>
      <Head icon={<Mail20Regular />} title="Inbox" sub="Mentions, assignments and updates on your work.">
        <Button
          disabled={!items.some((n) => !n.isRead)}
          onClick={async () => {
            if (await act(() => api("POST", "/api/notifications/read-all"))) after();
          }}
        >
          Mark all as read
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={unreadOnly ? "unread" : "all"} onTabSelect={(_, d) => setUnreadOnly(d.value === "unread")}>
          <Tab value="all">All</Tab>
          <Tab value="unread">Unread</Tab>
        </TabList>
      </div>
      <Page>
        <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && items.length === 0 && (unreadOnly ? "Nothing unread" : "Your inbox is empty")} onRetry={l.reload} />
        {items.map((n) => (
          <div key={n.id} className={`${px.item} ${n.isRead ? "" : px.itemUnread}`}>
            <span className={px.dot} aria-hidden="true" />
            <button type="button" className={px.itemMain} onClick={() => void open(n)}>
              <span className={px.itemTitle}>{n.title}</span>
              {n.body && <span className={px.itemBody}>{n.body}</span>}
              <span className={px.itemMeta}>
                {nice(n.type)} · {fmtStamp(n.createdAt)}
              </span>
            </button>
            <Button
              appearance="subtle"
              size="small"
              icon={<Delete20Regular />}
              aria-label="Delete notification"
              onClick={async () => {
                if (await act(() => api("DELETE", `/api/notifications/${n.id}`))) after();
              }}
            />
          </div>
        ))}
      </Page>
    </>
  );
};

/* =================================================================== *
 *  My Tasks
 * =================================================================== */
interface MyRow {
  id: number;
  projectId?: number | null;
  projectName?: string | null;
  title: string;
  priority: string;
  dueDate?: string | null;
  dueAt?: string | null;
  isCompleted: boolean;
}
const MY_FILTERS: [string, string][] = [
  ["open", "Open"],
  ["today", "Today"],
  ["due-soon", "Due soon"],
  ["overdue", "Overdue"],
  ["upcoming", "Upcoming"],
  ["no-date", "No date"],
  ["done", "Done"],
];

export const MyTasksPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [filter, setFilter] = useState("open");
  const l = useLoad(async () => asList<MyRow>(await api("GET", "/api/tasks/my", undefined, { filter, workspaceId: ctx.workspaceId, pageSize: 200 })), [filter, ctx.workspaceId]);
  const items = l.data ?? [];
  const today = iso(new Date());
  return (
    <>
      <Head icon={<PersonCircle20Regular />} title="My Tasks" sub="Everything assigned to you in this workspace." />
      <div className={styles.bar}>
        <TabList selectedValue={filter} onTabSelect={(_, d) => setFilter(String(d.value))}>
          {MY_FILTERS.map(([k, label]) => (
            <Tab key={k} value={k}>
              {label}
            </Tab>
          ))}
        </TabList>
      </div>
      <Page>
        <Wait loading={l.loading && !l.data} error={l.error} empty={!l.loading && items.length === 0 && "No tasks here"} onRetry={l.reload} />
        {items.length > 0 && (
          <table className={px.tbl}>
            <thead>
              <tr>
                <th>Task</th>
                <th>Project</th>
                <th>Priority</th>
                <th>Due</th>
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} className={px.clickRow} onClick={() => ctx.openTask(t.id)}>
                  <td className={px.strong}>{t.title}</td>
                  <td>{t.projectName ?? "—"}</td>
                  <td>
                    <Prio p={prioFromApi(t.priority)} />
                  </td>
                  <td className={!t.isCompleted && t.dueDate && t.dueDate.slice(0, 10) < today ? px.late : undefined}>{fmtDate(t.dueDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Page>
    </>
  );
};

/* =================================================================== *
 *  Time
 * =================================================================== */
interface Running {
  running: boolean;
  entry: { id: number; taskTitle?: string | null; startedAt: string; notes?: string | null } | null;
  elapsedMinutes: number;
}
interface Entry {
  id: number;
  taskTitle?: string | null;
  projectName?: string | null;
  workDate: string;
  minutes: number;
  isBillable: boolean;
  notes?: string | null;
}
interface Week {
  periodStart: string;
  periodEnd: string;
  sheet: { id: number; status: string; totalMinutes: number } | null;
  totalMinutes: number;
  billableMinutes: number;
  days: { date: string; minutes: number }[];
  entries: Entry[];
}
interface Pending {
  id: number;
  userId: number;
  displayName: string;
  periodStart: string;
  periodEnd: string;
  totalMinutes: number;
}
interface TimeRows {
  from: string;
  to: string;
  currency: string;
  totals: { minutes: number; hours: number; billableMinutes: number; amount: number };
  rows: { key: string; label: string; minutes: number; hours: number; billableMinutes: number; amount: number }[];
}

export const TimePage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [anchor, setAnchor] = useState(() => new Date());
  const [notes, setNotes] = useState("");
  const [timerProject, setTimerProject] = useState("");
  const [billable, setBillable] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [entry, setEntry] = useState({ date: iso(new Date()), hours: "1", projectId: "", notes: "", billable: false });
  const [now, setNow] = useState(() => Date.now());
  const isAdmin = ctx.me.workspaces.find((w) => w.id === ctx.workspaceId)?.role === "ADMIN";

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, []);

  const timer = useLoad(() => api<Running>("GET", "/api/time/timer/current"), []);
  const week = useLoad(() => api<Week>("GET", "/api/time/timesheets/week", undefined, { workspaceId: ctx.workspaceId, date: iso(anchor) }), [ctx.workspaceId, anchor.getTime()]);
  const pending = useLoad(async () => (isAdmin ? asList<Pending>(await api("GET", "/api/time/timesheets/pending", undefined, { workspaceId: ctx.workspaceId })) : []), [ctx.workspaceId, isAdmin]);

  const run = timer.data;
  const w = week.data;
  const elapsed = run?.entry ? Math.max(0, Math.floor((now - utc(run.entry.startedAt).getTime()) / 60000)) : 0;
  const maxDay = Math.max(60, ...(w?.days ?? []).map((d) => d.minutes));
  const editable = !w?.sheet || w.sheet.status === "OPEN" || w.sheet.status === "REJECTED";

  const refresh = () => {
    timer.reload();
    week.reload();
    pending.reload();
  };
  const shift = (n: number) => setAnchor((a) => new Date(a.getFullYear(), a.getMonth(), a.getDate() + n * 7));

  return (
    <>
      <Head icon={<Timer20Regular />} title="Time" sub="Track hours, then submit your week for approval.">
        <Button icon={<Add20Regular />} onClick={() => setAddOpen(true)}>
          Add time
        </Button>
      </Head>
      <Page>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Timer</h3>
          {timer.error && <Wait loading={false} error={timer.error} />}
          {run?.running && run.entry ? (
            <div className={px.rowBetween}>
              <div>
                <div className={px.bigNum}>{fmtMin(elapsed)}</div>
                <div className={styles.muted}>
                  {run.entry.taskTitle || run.entry.notes || "Running"} · started {fmtStamp(run.entry.startedAt)}
                </div>
              </div>
              <Button
                appearance="primary"
                className={styles.primary}
                icon={<Stop20Regular />}
                onClick={async () => {
                  if (await act(() => api("POST", "/api/time/timer/stop", {}), "Timer stopped.")) refresh();
                }}
              >
                Stop
              </Button>
            </div>
          ) : (
            <div className={px.formLine}>
              <Input className={styles.grow} placeholder="What are you working on?" value={notes} onChange={(_, d) => setNotes(d.value)} />
              <Select aria-label="Project" value={timerProject} onChange={(_, d) => setTimerProject(d.value)}>
                <option value="">No project</option>
                {ctx.projects.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name}
                  </option>
                ))}
              </Select>
              <Checkbox label="Billable" checked={billable} onChange={(_, d) => setBillable(d.checked === true)} />
              <Button
                appearance="primary"
                className={styles.primary}
                icon={<Play20Regular />}
                onClick={async () => {
                  const ok = await act(() =>
                    api("POST", "/api/time/timer/start", {
                      workspaceId: ctx.workspaceId,
                      projectId: timerProject ? Number(timerProject) : null,
                      notes: notes.trim() || null,
                      isBillable: billable,
                    }),
                  );
                  if (ok) {
                    setNotes("");
                    refresh();
                  }
                }}
              >
                Start
              </Button>
            </div>
          )}
        </div>

        <div className={px.card}>
          <div className={px.rowBetween}>
            <h3 className={px.cardTitle}>
              Week of {w ? fmtDate(w.periodStart) : "…"} – {w ? fmtDate(w.periodEnd) : ""}
            </h3>
            <div className={px.actions}>
              <Button size="small" onClick={() => shift(-1)}>
                Previous
              </Button>
              <Button size="small" onClick={() => setAnchor(new Date())}>
                This week
              </Button>
              <Button size="small" onClick={() => shift(1)}>
                Next
              </Button>
            </div>
          </div>
          <Wait loading={week.loading && !w} error={week.error} onRetry={week.reload} />
          {w && (
            <>
              <div className={px.tiles}>
                <Tile n={fmtMin(w.totalMinutes)} label="Total" />
                <Tile n={fmtMin(w.billableMinutes)} label="Billable" />
                <Tile n={w.sheet ? nice(w.sheet.status) : "Open"} label="Timesheet" />
              </div>
              <div className={px.dayBars}>
                {w.days.map((d) => (
                  <div key={d.date} className={px.dayBar}>
                    <div className={px.dayFill} style={{ height: `${Math.round((d.minutes / maxDay) * 100)}%` }} title={fmtMin(d.minutes)} />
                    <span className={px.dayLbl}>{["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][parseISO(d.date).getDay()]}</span>
                  </div>
                ))}
              </div>
              {w.entries.length === 0 ? (
                <div className={styles.emptyRow}>No time logged this week</div>
              ) : (
                <table className={px.tbl}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>What</th>
                      <th>Project</th>
                      <th>Time</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {w.entries.map((e) => (
                      <tr key={e.id}>
                        <td>{fmtDate(e.workDate)}</td>
                        <td>
                          {e.taskTitle || e.notes || "—"} {e.isBillable && <Chip text="Billable" />}
                        </td>
                        <td>{e.projectName ?? "—"}</td>
                        <td>{fmtMin(e.minutes)}</td>
                        <td>
                          {editable && (
                            <Button
                              appearance="subtle"
                              size="small"
                              icon={<Delete20Regular />}
                              aria-label="Delete entry"
                              onClick={async () => {
                                if (await act(() => crud.remove(R.timeEntry, e.id))) refresh();
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {w.sheet && editable && w.entries.length > 0 && (
                <div className={px.actions}>
                  <Button
                    appearance="primary"
                    className={styles.primary}
                    onClick={async () => {
                      if (await act(() => api("POST", `/api/time/timesheets/${w.sheet?.id}/submit`), "Timesheet submitted.")) refresh();
                    }}
                  >
                    Submit week
                  </Button>
                </div>
              )}
            </>
          )}
        </div>

        {isAdmin && (pending.data ?? []).length > 0 && (
          <div className={px.card}>
            <h3 className={px.cardTitle}>Waiting for your approval</h3>
            <table className={px.tbl}>
              <tbody>
                {(pending.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className={px.strong}>{p.displayName}</td>
                    <td>
                      {fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}
                    </td>
                    <td>{fmtMin(p.totalMinutes)}</td>
                    <td className={px.actions}>
                      <Button
                        size="small"
                        appearance="primary"
                        className={styles.primary}
                        onClick={async () => {
                          if (await act(() => api("POST", `/api/time/timesheets/${p.id}/approve`), "Approved.")) refresh();
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        onClick={async () => {
                          if (await act(() => api("POST", `/api/time/timesheets/${p.id}/reject`, { reason: "Please review and resubmit." }), "Sent back.")) refresh();
                        }}
                      >
                        Send back
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <TimeSummary ctx={ctx} />
      </Page>

      <FormDialog
        open={addOpen}
        title="Add time"
        action="Add"
        disabled={!(Number(entry.hours) > 0)}
        onClose={() => setAddOpen(false)}
        onSubmit={async () => {
          const ok = await act(
            () =>
              crud.create(R.timeEntry, {
                workspaceId: ctx.workspaceId,
                userId: ctx.me.id,
                projectId: entry.projectId ? Number(entry.projectId) : null,
                workDate: entry.date,
                minutes: Math.round(Number(entry.hours) * 60),
                notes: entry.notes.trim() || null,
                isBillable: entry.billable,
              }),
            "Time added.",
          );
          if (ok) {
            setAddOpen(false);
            refresh();
          }
        }}
      >
        <div className={styles.formRow}>
          <Field label="Date" className={styles.grow}>
            <Input type="date" value={entry.date} onChange={(_, d) => setEntry({ ...entry, date: d.value })} />
          </Field>
          <Field label="Hours">
            <Input type="number" min="0" step="0.25" value={entry.hours} onChange={(_, d) => setEntry({ ...entry, hours: d.value })} />
          </Field>
        </div>
        <Field label="Project">
          <Select value={entry.projectId} onChange={(_, d) => setEntry({ ...entry, projectId: d.value })}>
            <option value="">No project</option>
            {ctx.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Notes">
          <Input value={entry.notes} onChange={(_, d) => setEntry({ ...entry, notes: d.value })} />
        </Field>
        <Checkbox label="Billable" checked={entry.billable} onChange={(_, d) => setEntry({ ...entry, billable: d.checked === true })} />
      </FormDialog>
    </>
  );
};

const TimeSummary: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const [from, setFrom] = useState(() => iso(mondayOf(new Date())));
  const [to, setTo] = useState(() => iso(new Date()));
  const [groupBy, setGroupBy] = useState("project");
  const l = useLoad(() => api<TimeRows>("GET", "/api/time/reports/summary", undefined, { workspaceId: ctx.workspaceId, from, to, groupBy }), [ctx.workspaceId, from, to, groupBy]);
  const d = l.data;
  return (
    <div className={px.card}>
      <div className={px.rowBetween}>
        <h3 className={px.cardTitle}>Time report</h3>
        <div className={px.actions}>
          <Input type="date" value={from} onChange={(_, o) => o.value && setFrom(o.value)} aria-label="From" />
          <Input type="date" value={to} onChange={(_, o) => o.value && setTo(o.value)} aria-label="To" />
          <Select aria-label="Group by" value={groupBy} onChange={(_, o) => setGroupBy(o.value)}>
            {["project", "user", "task", "day", "client"].map((g) => (
              <option key={g} value={g}>
                By {g}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <Wait loading={l.loading && !d} error={l.error} onRetry={l.reload} />
      {d && (
        <>
          <div className={px.tiles}>
            <Tile n={fmtMin(d.totals.minutes)} label="Total" />
            <Tile n={fmtMin(d.totals.billableMinutes)} label="Billable" />
            <Tile n={`${d.totals.amount.toLocaleString()} ${d.currency}`} label="Amount" />
          </div>
          {d.rows.length === 0 ? (
            <div className={styles.emptyRow}>No time in this period</div>
          ) : (
            <table className={px.tbl}>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.key}>
                    <td className={px.strong}>{r.label || r.key}</td>
                    <td>{fmtMin(r.minutes)}</td>
                    <td>{fmtMin(r.billableMinutes)} billable</td>
                    <td>
                      {r.amount.toLocaleString()} {d.currency}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
};

/* =================================================================== *
 *  Goals
 * =================================================================== */
interface KeyResult {
  id: number;
  name: string;
  startValue: number;
  targetValue: number;
  currentValue: number;
  unit?: string | null;
}
interface GoalNode {
  id: number;
  parentGoalId?: number | null;
  name: string;
  goalLevel: string;
  status: string;
  progressPercent: number;
  progressMode: string;
  ownerName?: string | null;
  period?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  keyResults: KeyResult[] | null;
  children: GoalNode[] | null;
}
interface GoalSummary {
  total: number;
  averageProgress: number;
  byStatus: Record<string, number> | { key: string; count: number }[] | null;
  needsAttention: unknown;
}

const GoalCard: React.FC<{ g: GoalNode; depth: number; onChange: () => void; onAddKr: (g: GoalNode) => void; act: ReturnType<typeof useAct> }> = ({ g, depth, onChange, onAddKr, act }) => (
  <div className={px.goal} style={{ marginLeft: depth * 20 }}>
    <div className={px.rowBetween}>
      <div>
        <div className={px.itemTitle}>{g.name}</div>
        <div className={styles.muted}>
          {nice(g.goalLevel)}
          {g.ownerName ? ` · ${g.ownerName}` : ""}
          {g.endDate ? ` · until ${fmtDate(g.endDate)}` : ""}
        </div>
      </div>
      <div className={px.actions}>
        <Chip text={g.status} />
        <Button size="small" onClick={() => onAddKr(g)}>
          Add key result
        </Button>
      </div>
    </div>
    <div className={px.progressRow}>
      <ProgressBar value={Math.max(0, Math.min(100, g.progressPercent)) / 100} className={px.grow} />
      <span className={px.pct}>{Math.round(g.progressPercent)}%</span>
    </div>
    {(g.keyResults ?? []).map((k) => (
      <div key={k.id} className={px.kr}>
        <span className={px.grow}>{k.name}</span>
        <Input
          type="number"
          size="small"
          aria-label={`Current value of ${k.name}`}
          defaultValue={String(k.currentValue)}
          onBlur={async (e) => {
            const v = Number(e.currentTarget.value);
            if (Number.isNaN(v) || v === k.currentValue) return;
            if (await act(() => crud.update(R.keyResult, k.id, { currentValue: v }))) {
              await act(() => api("POST", `/api/goals/${g.id}/recalculate`));
              onChange();
            }
          }}
        />
        <span className={styles.muted}>
          / {k.targetValue} {k.unit ?? ""}
        </span>
      </div>
    ))}
    {(g.children ?? []).map((c) => (
      <GoalCard key={c.id} g={c} depth={depth + 1} onChange={onChange} onAddKr={onAddKr} act={act} />
    ))}
  </div>
);

export const GoalsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [mine, setMine] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [krFor, setKrFor] = useState<GoalNode | null>(null);
  const [g, setG] = useState({ name: "", level: "TEAM", parent: "", start: iso(new Date()), end: "" });
  const [kr, setKr] = useState({ name: "", target: "100", unit: "" });
  const tree = useLoad(() => api<{ goals: GoalNode[] }>("GET", "/api/goals/tree", undefined, { workspaceId: ctx.workspaceId, mine }), [ctx.workspaceId, mine]);
  const sum = useLoad(() => api<GoalSummary>("GET", "/api/goals/summary", undefined, { workspaceId: ctx.workspaceId }), [ctx.workspaceId]);
  const goals = tree.data?.goals ?? [];
  const flat = useMemo(() => {
    const out: GoalNode[] = [];
    const walk = (list: GoalNode[]) => list.forEach((n) => (out.push(n), walk(n.children ?? [])));
    walk(goals);
    return out;
  }, [goals]);
  const reload = () => {
    tree.reload();
    sum.reload();
  };
  const statusCount = (k: string) => {
    const b = sum.data?.byStatus;
    if (!b) return 0;
    if (Array.isArray(b)) return b.find((x) => x.key.toUpperCase() === k)?.count ?? 0;
    return Object.entries(b).find(([key]) => key.toUpperCase() === k)?.[1] ?? 0;
  };

  return (
    <>
      <Head icon={<Trophy20Regular />} title="Goals" sub="Objectives, key results and how far along they are.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setGoalOpen(true)}>
          New goal
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={mine ? "mine" : "all"} onTabSelect={(_, d) => setMine(d.value === "mine")}>
          <Tab value="all">All goals</Tab>
          <Tab value="mine">Mine</Tab>
        </TabList>
      </div>
      <Page>
        {sum.data && (
          <div className={px.tiles}>
            <Tile n={sum.data.total} label="Goals" />
            <Tile n={`${Math.round(sum.data.averageProgress)}%`} label="Average progress" />
            <Tile n={statusCount("ON_TRACK")} label="On track" />
            <Tile n={statusCount("AT_RISK") + statusCount("OFF_TRACK")} label="Need attention" />
          </div>
        )}
        <Wait loading={tree.loading && !tree.data} error={tree.error} empty={!tree.loading && goals.length === 0 && "No goals yet"} onRetry={tree.reload} />
        {goals.map((n) => (
          <GoalCard key={n.id} g={n} depth={0} onChange={reload} onAddKr={(x) => setKrFor(x)} act={act} />
        ))}
      </Page>

      <FormDialog
        open={goalOpen}
        title="New goal"
        action="Create goal"
        disabled={!g.name.trim()}
        onClose={() => setGoalOpen(false)}
        onSubmit={async () => {
          const ok = await act(
            () =>
              crud.create(R.goal, {
                workspaceId: ctx.workspaceId,
                name: g.name.trim(),
                goalLevel: g.level,
                parentGoalId: g.parent ? Number(g.parent) : null,
                ownerUserId: ctx.me.id,
                startDate: g.start || null,
                endDate: g.end || null,
              }),
            "Goal created.",
          );
          if (ok) {
            setGoalOpen(false);
            setG({ name: "", level: "TEAM", parent: "", start: iso(new Date()), end: "" });
            reload();
          }
        }}
      >
        <Field label="Name" required>
          <Input value={g.name} onChange={(_, d) => setG({ ...g, name: d.value })} placeholder="Grow active users by 20%" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Level" className={styles.grow}>
            <Select value={g.level} onChange={(_, d) => setG({ ...g, level: d.value })}>
              {["COMPANY", "TEAM", "INDIVIDUAL"].map((x) => (
                <option key={x} value={x}>
                  {nice(x)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Part of" className={styles.grow}>
            <Select value={g.parent} onChange={(_, d) => setG({ ...g, parent: d.value })}>
              <option value="">Nothing (top level)</option>
              {flat.map((x) => (
                <option key={x.id} value={String(x.id)}>
                  {x.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className={styles.formRow}>
          <Field label="Start" className={styles.grow}>
            <Input type="date" value={g.start} onChange={(_, d) => setG({ ...g, start: d.value })} />
          </Field>
          <Field label="End" className={styles.grow}>
            <Input type="date" value={g.end} onChange={(_, d) => setG({ ...g, end: d.value })} />
          </Field>
        </div>
      </FormDialog>

      <FormDialog
        open={krFor !== null}
        title={krFor ? `Key result for “${krFor.name}”` : "Key result"}
        action="Add"
        disabled={!kr.name.trim() || !(Number(kr.target) > 0)}
        onClose={() => setKrFor(null)}
        onSubmit={async () => {
          if (!krFor) return;
          const ok = await act(
            () => crud.create(R.keyResult, { workspaceId: ctx.workspaceId, goalId: krFor.id, name: kr.name.trim(), targetValue: Number(kr.target), unit: kr.unit.trim() || null }),
            "Key result added.",
          );
          if (ok) {
            await act(() => api("POST", `/api/goals/${krFor.id}/recalculate`));
            setKrFor(null);
            setKr({ name: "", target: "100", unit: "" });
            reload();
          }
        }}
      >
        <Field label="Name" required>
          <Input value={kr.name} onChange={(_, d) => setKr({ ...kr, name: d.value })} placeholder="Weekly active users" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Target" className={styles.grow}>
            <Input type="number" value={kr.target} onChange={(_, d) => setKr({ ...kr, target: d.value })} />
          </Field>
          <Field label="Unit" className={styles.grow}>
            <Input value={kr.unit} onChange={(_, d) => setKr({ ...kr, unit: d.value })} placeholder="users, %, $…" />
          </Field>
        </div>
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Sprints
 * =================================================================== */
interface SprintRow {
  id: number;
  projectId: number;
  name: string;
  goal?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  status: string;
  committedPoints?: number | null;
  completedPoints?: number | null;
}
interface BurnDay {
  date: string;
  ideal: number;
  remaining: number;
}
interface Velocity {
  averageVelocity: number;
  forecastNextSprint: number;
  commitmentAccuracy: number | null;
  sprints: { id: number; name: string; committed: number; completed: number }[];
}
interface Backlog {
  count: number;
  points: number;
  unestimated: number;
  items: { id: number; title: string; priority: string; storyPoints?: number | null }[];
}

const BurnChart: React.FC<{ days: BurnDay[] }> = ({ days }) => {
  if (days.length < 2) return <div className={styles.emptyRow}>The chart appears once the sprint has a few days of data</div>;
  const W = 560;
  const H = 180;
  const P = 28;
  const max = Math.max(1, ...days.map((d) => Math.max(d.ideal, d.remaining)));
  const x = (i: number) => P + (i / (days.length - 1)) * (W - P * 2);
  const y = (v: number) => H - P - (v / max) * (H - P * 2);
  const line = (f: (d: BurnDay) => number) => days.map((d, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(f(d)).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={px.chart} role="img" aria-label="Sprint burndown: remaining points against the ideal line">
      <line x1={P} y1={H - P} x2={W - P} y2={H - P} stroke="#d1d1d1" />
      <line x1={P} y1={P} x2={P} y2={H - P} stroke="#d1d1d1" />
      <path d={line((d) => d.ideal)} fill="none" stroke="#a6a6a6" strokeWidth="2" strokeDasharray="5 4" />
      <path d={line((d) => d.remaining)} fill="none" stroke="#0f6cbd" strokeWidth="2.5" />
      <text x={P} y={14} fontSize="11" fill="#616161">
        {Math.round(max)} pts
      </text>
      <text x={W - P} y={H - 8} fontSize="11" fill="#616161" textAnchor="end">
        {fmtDate(days[days.length - 1]?.date)}
      </text>
      <text x={P} y={H - 8} fontSize="11" fill="#616161">
        {fmtDate(days[0]?.date)}
      </text>
    </svg>
  );
};

export const SprintsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [pid, setPid] = useState<number | null>(ctx.projectId ?? ctx.projects[0]?.id ?? null);
  const [sel, setSel] = useState<number | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [startFor, setStartFor] = useState<SprintRow | null>(null);
  const [form, setForm] = useState({ name: "", goal: "" });
  const [range, setRange] = useState({ start: iso(new Date()), end: iso(new Date(Date.now() + 13 * 86400000)) });

  const sprints = useLoad(async () => (pid === null ? [] : (await crud.list<SprintRow>(R.sprint, { projectId: pid, sort: "-startDate" })).items), [pid]);
  const velocity = useLoad(() => (pid === null ? Promise.resolve(null) : api<Velocity>("GET", `/api/agile/projects/${pid}/velocity`, undefined, { last: 6 })), [pid]);
  const backlog = useLoad(() => (pid === null ? Promise.resolve(null) : api<Backlog>("GET", `/api/agile/projects/${pid}/backlog`)), [pid]);
  const list = sprints.data ?? [];
  const shown = list.find((s) => s.id === sel) ?? list.find((s) => s.status === "ACTIVE") ?? list[0] ?? null;
  const burn = useLoad(
    () => (shown ? api<{ days: BurnDay[] }>("GET", `/api/agile/sprints/${shown.id}/burndown`) : Promise.resolve(null)),
    [shown?.id, shown?.status],
  );
  const reload = () => {
    sprints.reload();
    velocity.reload();
    backlog.reload();
    burn.reload();
  };

  return (
    <>
      <Head icon={<Rocket20Regular />} title="Sprints" sub="Plan sprints, watch the burndown and see how fast the team moves.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} disabled={pid === null} onClick={() => setNewOpen(true)}>
          New sprint
        </Button>
      </Head>
      <div className={styles.bar}>
        <Select aria-label="Project" value={pid === null ? "" : String(pid)} onChange={(_, d) => (setPid(d.value ? Number(d.value) : null), setSel(null))}>
          {ctx.projects.length === 0 && <option value="">No projects</option>}
          {ctx.projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name}
            </option>
          ))}
        </Select>
      </div>
      <Page>
        {velocity.data && (
          <div className={px.tiles}>
            <Tile n={velocity.data.averageVelocity} label="Average velocity (pts)" />
            <Tile n={velocity.data.forecastNextSprint} label="Forecast next sprint" />
            <Tile n={velocity.data.commitmentAccuracy === null ? "—" : `${velocity.data.commitmentAccuracy}%`} label="Commitment accuracy" />
            <Tile n={backlog.data ? `${backlog.data.count} · ${backlog.data.points} pts` : "—"} label="Backlog" />
          </div>
        )}
        <Wait loading={sprints.loading && !sprints.data} error={sprints.error} empty={!sprints.loading && list.length === 0 && "No sprints yet"} onRetry={sprints.reload} />
        {list.map((s) => (
          <div key={s.id} className={`${px.item} ${shown?.id === s.id ? px.itemPicked : ""}`}>
            <button type="button" className={px.itemMain} onClick={() => setSel(s.id)}>
              <span className={px.itemTitle}>
                {s.name} <Chip text={s.status} />
              </span>
              {s.goal && <span className={px.itemBody}>{s.goal}</span>}
              <span className={px.itemMeta}>
                {fmtDate(s.startDate)} – {fmtDate(s.endDate)} · {s.completedPoints ?? 0}/{s.committedPoints ?? 0} pts
              </span>
            </button>
            {s.status === "PLANNED" && (
              <Button size="small" onClick={() => setStartFor(s)}>
                Start
              </Button>
            )}
            {s.status === "ACTIVE" && (
              <Button
                size="small"
                onClick={async () => {
                  if (await act(() => api("POST", `/api/agile/sprints/${s.id}/complete`, {}), "Sprint completed. Unfinished work went back to the backlog.")) reload();
                }}
              >
                Complete
              </Button>
            )}
          </div>
        ))}
        {shown && (
          <div className={px.card}>
            <h3 className={px.cardTitle}>Burndown · {shown.name}</h3>
            <Wait loading={burn.loading && !burn.data} error={burn.error} />
            {burn.data && <BurnChart days={burn.data.days ?? []} />}
          </div>
        )}
        {backlog.data && backlog.data.items.length > 0 && (
          <div className={px.card}>
            <h3 className={px.cardTitle}>Backlog ({backlog.data.count})</h3>
            <table className={px.tbl}>
              <tbody>
                {backlog.data.items.slice(0, 30).map((t) => (
                  <tr key={t.id} className={px.clickRow} onClick={() => ctx.openTask(t.id)}>
                    <td className={px.strong}>{t.title}</td>
                    <td>
                      <Prio p={prioFromApi(t.priority)} />
                    </td>
                    <td>{t.storyPoints ?? "—"} pts</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Page>

      <FormDialog
        open={newOpen}
        title="New sprint"
        action="Create sprint"
        disabled={!form.name.trim()}
        onClose={() => setNewOpen(false)}
        onSubmit={async () => {
          if (pid === null) return;
          const ok = await act(() => crud.create(R.sprint, { workspaceId: ctx.workspaceId, projectId: pid, name: form.name.trim(), goal: form.goal.trim() || null }), "Sprint created.");
          if (ok) {
            setNewOpen(false);
            setForm({ name: "", goal: "" });
            reload();
          }
        }}
      >
        <Field label="Name" required>
          <Input value={form.name} onChange={(_, d) => setForm({ ...form, name: d.value })} placeholder="Sprint 12" />
        </Field>
        <Field label="Goal">
          <Textarea value={form.goal} onChange={(_, d) => setForm({ ...form, goal: d.value })} rows={2} resize="vertical" />
        </Field>
      </FormDialog>

      <FormDialog
        open={startFor !== null}
        title={startFor ? `Start ${startFor.name}` : "Start sprint"}
        action="Start sprint"
        disabled={!range.start || !range.end || range.end < range.start}
        onClose={() => setStartFor(null)}
        onSubmit={async () => {
          if (!startFor) return;
          if (await act(() => api("POST", `/api/agile/sprints/${startFor.id}/start`, { startDate: range.start, endDate: range.end }), "Sprint started.")) {
            setStartFor(null);
            reload();
          }
        }}
      >
        <div className={styles.formRow}>
          <Field label="Starts" className={styles.grow}>
            <Input type="date" value={range.start} onChange={(_, d) => setRange({ ...range, start: d.value })} />
          </Field>
          <Field label="Ends" className={styles.grow}>
            <Input type="date" value={range.end} onChange={(_, d) => setRange({ ...range, end: d.value })} />
          </Field>
        </div>
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Docs
 * =================================================================== */
interface DocNode {
  id: number;
  title: string;
  docType: string;
  isPublished: boolean;
  children: DocNode[] | null;
}
interface Doc {
  id: number;
  title: string;
  body?: string | null;
  isPublished: boolean;
  version: number;
  updatedAt?: string | null;
}
interface DocVersion {
  versionNo: number;
  title?: string | null;
  changeNote?: string | null;
  createdAt?: string | null;
}
interface DocHit {
  id: number;
  title: string;
  snippet?: string | null;
}

const DocTree: React.FC<{ nodes: DocNode[]; depth: number; selected: number | null; onPick: (id: number) => void }> = ({ nodes, depth, selected, onPick }) => (
  <>
    {nodes.map((n) => (
      <React.Fragment key={n.id}>
        <button type="button" className={`${px.treeItem} ${selected === n.id ? px.treeOn : ""}`} style={{ paddingLeft: 10 + depth * 16 }} onClick={() => onPick(n.id)}>
          {n.title}
          {!n.isPublished && <span className={px.draft}>draft</span>}
        </button>
        <DocTree nodes={n.children ?? []} depth={depth + 1} selected={selected} onPick={onPick} />
      </React.Fragment>
    ))}
  </>
);

export const DocsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [pick, setPick] = useState<number | null>(null);
  const [edit, setEdit] = useState<{ title: string; body: string; published: boolean } | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [q, setQ] = useState("");
  const [versionsOpen, setVersionsOpen] = useState(false);
  const tree = useLoad(async () => asList<DocNode>(await api("GET", "/api/knowledge/tree", undefined, { workspaceId: ctx.workspaceId })), [ctx.workspaceId]);
  const doc = useLoad(() => (pick === null ? Promise.resolve(null) : crud.get<Doc>(R.document, pick)), [pick]);
  const hits = useLoad(
    async () => (q.trim().length >= 2 ? asList<DocHit>(await api("GET", "/api/knowledge/search", undefined, { workspaceId: ctx.workspaceId, q: q.trim() })) : []),
    [q, ctx.workspaceId],
  );
  const versions = useLoad(async () => (versionsOpen && pick !== null ? asList<DocVersion>(await api("GET", `/api/knowledge/documents/${pick}/versions`)) : []), [versionsOpen, pick]);
  const d = doc.data;
  const searching = q.trim().length >= 2;

  return (
    <>
      <Head icon={<Library20Regular />} title="Docs" sub="Pages, notes and how-tos for your workspace.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setNewOpen(true)}>
          New page
        </Button>
      </Head>
      <div className={styles.body}>
        <div className={px.split}>
          <nav className={px.splitSide} aria-label="Pages">
            <SearchBox className={styles.search} placeholder="Search docs" value={q} onChange={(_, o) => setQ(o.value)} />
            {!searching && <Wait loading={tree.loading && !tree.data} error={tree.error} empty={!tree.loading && (tree.data ?? []).length === 0 && "No pages yet"} onRetry={tree.reload} />}
            {!searching && <DocTree nodes={tree.data ?? []} depth={0} selected={pick} onPick={(id) => (setPick(id), setEdit(null))} />}
            {searching &&
              (hits.data ?? []).map((h) => (
                <button key={h.id} type="button" className={`${px.treeItem} ${pick === h.id ? px.treeOn : ""}`} onClick={() => (setPick(h.id), setEdit(null))}>
                  <span>{h.title}</span>
                  {h.snippet && <span className={px.snippet}>{h.snippet}</span>}
                </button>
              ))}
            {searching && !hits.loading && (hits.data ?? []).length === 0 && <div className={styles.emptyRow}>No matches</div>}
          </nav>
          <article className={px.splitMain}>
            {pick === null && <div className={styles.emptyRow}>Pick a page on the left</div>}
            {pick !== null && <Wait loading={doc.loading && !d} error={doc.error} onRetry={doc.reload} />}
            {d && !edit && (
              <>
                <div className={px.rowBetween}>
                  <h2 className={px.docTitle}>{d.title}</h2>
                  <div className={px.actions}>
                    <Button size="small" onClick={() => setVersionsOpen(true)}>
                      History (v{d.version})
                    </Button>
                    <Button size="small" onClick={() => setEdit({ title: d.title, body: d.body ?? "", published: d.isPublished })}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      icon={<Delete20Regular />}
                      aria-label="Delete page"
                      onClick={async () => {
                        if (await act(() => crud.remove(R.document, d.id), "Page deleted.")) {
                          setPick(null);
                          tree.reload();
                        }
                      }}
                    />
                  </div>
                </div>
                <div className={styles.muted}>
                  {d.isPublished ? "Published" : "Draft"} · updated {fmtStamp(d.updatedAt)}
                </div>
                <pre className={px.docBody}>{d.body || "This page is empty."}</pre>
              </>
            )}
            {d && edit && (
              <>
                <Field label="Title">
                  <Input value={edit.title} onChange={(_, o) => setEdit({ ...edit, title: o.value })} />
                </Field>
                <Field label="Content">
                  <Textarea value={edit.body} onChange={(_, o) => setEdit({ ...edit, body: o.value })} rows={16} resize="vertical" />
                </Field>
                <Switch label="Published" checked={edit.published} onChange={(_, o) => setEdit({ ...edit, published: o.checked })} />
                <div className={px.actions}>
                  <Button
                    appearance="primary"
                    className={styles.primary}
                    disabled={!edit.title.trim()}
                    onClick={async () => {
                      if (await act(() => crud.update(R.document, d.id, { title: edit.title.trim(), body: edit.body, isPublished: edit.published }), "Saved.")) {
                        setEdit(null);
                        doc.reload();
                        tree.reload();
                      }
                    }}
                  >
                    Save
                  </Button>
                  <Button onClick={() => setEdit(null)}>Cancel</Button>
                </div>
              </>
            )}
          </article>
        </div>
      </div>

      <FormDialog
        open={newOpen}
        title="New page"
        action="Create page"
        disabled={!newTitle.trim()}
        onClose={() => setNewOpen(false)}
        onSubmit={async () => {
          try {
            const created = await crud.create<{ id: number }>(R.document, {
              workspaceId: ctx.workspaceId,
              title: newTitle.trim(),
              body: "",
              docType: "PAGE",
              bodyFormat: "MARKDOWN",
              parentDocumentId: pick,
              projectId: ctx.projectId,
            });
            setNewOpen(false);
            setNewTitle("");
            tree.reload();
            setPick(created.id);
            setEdit({ title: newTitle.trim(), body: "", published: false });
          } catch (e) {
            ctx.notify("error", msgOf(e));
          }
        }}
      >
        <Field label="Title" required hint={pick !== null ? "It will be created inside the page you have open." : null}>
          <Input value={newTitle} onChange={(_, o) => setNewTitle(o.value)} placeholder="Release checklist" />
        </Field>
      </FormDialog>

      <Dialog open={versionsOpen} onOpenChange={(_, o) => !o.open && setVersionsOpen(false)}>
        <DialogSurface className={styles.dialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Page history
            </DialogTitle>
            <DialogContent className={styles.formBody}>
              <Wait loading={versions.loading} error={versions.error} empty={!versions.loading && (versions.data ?? []).length === 0 && "No earlier versions"} />
              {(versions.data ?? []).map((v) => (
                <div key={v.versionNo} className={px.item}>
                  <div className={px.itemMain}>
                    <span className={px.itemTitle}>
                      Version {v.versionNo}
                      {v.title ? ` · ${v.title}` : ""}
                    </span>
                    <span className={px.itemMeta}>
                      {fmtStamp(v.createdAt)}
                      {v.changeNote ? ` · ${v.changeNote}` : ""}
                    </span>
                  </div>
                  <Button
                    size="small"
                    onClick={async () => {
                      if (pick !== null && (await act(() => api("POST", `/api/knowledge/documents/${pick}/restore/${v.versionNo}`), "Restored."))) {
                        setVersionsOpen(false);
                        doc.reload();
                      }
                    }}
                  >
                    Restore
                  </Button>
                </div>
              ))}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* =================================================================== *
 *  Reports
 * =================================================================== */
interface Catalog {
  reportTypes: string[];
  datasets: string[];
}
interface ReportOut {
  type: string;
  columns: string[];
  rows: unknown[][];
  count: number;
}

export const ReportsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [type, setType] = useState("TASK_COMPLETION");
  const [project, setProject] = useState("");
  const [out, setOut] = useState<ReportOut | null>(null);
  const [busy, setBusy] = useState(false);
  const cat = useLoad(() => api<Catalog>("GET", "/api/reports/catalog"), []);
  const types = cat.data?.reportTypes ?? [];

  const run = async () => {
    setBusy(true);
    const ok = await act(async () => {
      setOut(await api<ReportOut>("POST", "/api/reports/run", { reportType: type, workspaceId: ctx.workspaceId, definition: project ? { projectId: Number(project) } : {} }));
    });
    setBusy(false);
    return ok;
  };

  return (
    <>
      <Head icon={<DataBarVertical20Regular />} title="Reports" sub="Run a built-in report and take the numbers with you.">
        {out && (
          <Button icon={<ArrowDownload20Regular />} onClick={() => downloadCsv(`${out.type.toLowerCase()}-${iso(new Date())}.csv`, out.columns, out.rows)}>
            Download CSV
          </Button>
        )}
      </Head>
      <div className={styles.bar}>
        <div className={px.actions}>
          <Select aria-label="Report" value={type} onChange={(_, d) => setType(d.value)}>
            {(types.length ? types : ["TASK_COMPLETION"]).map((t) => (
              <option key={t} value={t}>
                {nice(t)}
              </option>
            ))}
          </Select>
          <Select aria-label="Project" value={project} onChange={(_, d) => setProject(d.value)}>
            <option value="">All projects</option>
            {ctx.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
          <Button className={styles.primary} appearance="primary" disabled={busy} onClick={() => void run()}>
            {busy ? "Running…" : "Run report"}
          </Button>
        </div>
      </div>
      <Page>
        <Wait loading={cat.loading && !cat.data} error={cat.error} onRetry={cat.reload} />
        {!out && !cat.loading && <div className={styles.emptyRow}>Pick a report and press Run</div>}
        {out && (
          <div className={px.card}>
            <h3 className={px.cardTitle}>
              {nice(out.type)} · {out.count} {out.count === 1 ? "row" : "rows"}
            </h3>
            {out.rows.length === 0 ? (
              <div className={styles.emptyRow}>Nothing to show for this report</div>
            ) : (
              <div className={px.scroll}>
                <table className={px.tbl}>
                  <thead>
                    <tr>
                      {out.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {out.rows.map((r, i) => (
                      <tr key={i}>
                        {r.map((v, j) => (
                          <td key={j}>{v === null || v === undefined ? "—" : String(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </Page>
    </>
  );
};

/* =================================================================== *
 *  Tickets
 * =================================================================== */
interface TicketRow {
  id: number;
  ticketNumber: number;
  title: string;
  ticketType: string;
  priority: string;
  status: string;
  assigneeUserId?: number | null;
  assigneeName?: string | null;
  resolutionDueAt?: string | null;
  breachedAt?: string | null;
}
interface TicketStats {
  byStatus: { key: string; count: number }[];
  period: { created?: number; resolved?: number; breached?: number } | null;
}
const TICKET_VIEWS: [string, string][] = [
  ["open", "Open"],
  ["mine", "Assigned to me"],
  ["unassigned", "Unassigned"],
  ["all", "All"],
];

export const TicketsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [view, setView] = useState("open");
  const [q, setQ] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [resolveFor, setResolveFor] = useState<TicketRow | null>(null);
  const [resolution, setResolution] = useState("");
  const [f, setF] = useState({ title: "", description: "", type: "INCIDENT", priority: "MEDIUM" });
  const list = useLoad(async () => asList<TicketRow>(await api("GET", "/api/intake/tickets/queue", undefined, { view, workspaceId: ctx.workspaceId, q: q.trim() || undefined, pageSize: 100 })), [view, q, ctx.workspaceId]);
  const stats = useLoad(() => api<TicketStats>("GET", "/api/intake/tickets/stats", undefined, { workspaceId: ctx.workspaceId, days: 30 }), [ctx.workspaceId]);
  const rows = list.data ?? [];
  const reload = () => {
    list.reload();
    stats.reload();
  };
  const now = Date.now();
  const stat = (k: string) => stats.data?.byStatus?.find((s) => s.key.toUpperCase() === k)?.count ?? 0;

  return (
    <>
      <Head icon={<Headset20Regular />} title="Tickets" sub="Requests and incidents, with their response targets.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setNewOpen(true)}>
          New ticket
        </Button>
      </Head>
      <div className={styles.bar}>
        <TabList selectedValue={view} onTabSelect={(_, d) => setView(String(d.value))}>
          {TICKET_VIEWS.map(([k, label]) => (
            <Tab key={k} value={k}>
              {label}
            </Tab>
          ))}
        </TabList>
        <div className={styles.tools}>
          <SearchBox className={styles.search} placeholder="Search" value={q} onChange={(_, o) => setQ(o.value)} />
        </div>
      </div>
      <Page>
        {stats.data && (
          <div className={px.tiles}>
            <Tile n={stat("NEW") + stat("OPEN") + stat("IN_PROGRESS")} label="Open" />
            <Tile n={stats.data.period?.created ?? 0} label="Created (30 days)" />
            <Tile n={stats.data.period?.resolved ?? 0} label="Resolved (30 days)" />
            <Tile n={stats.data.period?.breached ?? 0} label="Missed target" />
          </div>
        )}
        <Wait loading={list.loading && !list.data} error={list.error} empty={!list.loading && rows.length === 0 && "No tickets here"} onRetry={list.reload} />
        {rows.length > 0 && (
          <div className={px.scroll}>
            <table className={px.tbl}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Assignee</th>
                  <th>Due</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const closed = t.status === "RESOLVED" || t.status === "CLOSED";
                  const late = !closed && (t.breachedAt || (t.resolutionDueAt && utc(t.resolutionDueAt).getTime() < now));
                  return (
                    <tr key={t.id}>
                      <td>{t.ticketNumber}</td>
                      <td className={px.strong}>{t.title}</td>
                      <td>
                        <Chip text={t.priority} />
                      </td>
                      <td>
                        <Chip text={t.status} />
                      </td>
                      <td>{t.assigneeName ?? "—"}</td>
                      <td className={late ? px.late : undefined}>{fmtStamp(t.resolutionDueAt)}</td>
                      <td className={px.actions}>
                        {!closed && !t.assigneeUserId && (
                          <Button
                            size="small"
                            onClick={async () => {
                              if (await act(() => api("POST", `/api/intake/tickets/${t.id}/assign`, { userId: ctx.me.id }), "Assigned to you.")) reload();
                            }}
                          >
                            Take
                          </Button>
                        )}
                        {!closed && (
                          <Button size="small" onClick={() => (setResolveFor(t), setResolution(""))}>
                            Resolve
                          </Button>
                        )}
                        {!closed && (
                          <Button
                            size="small"
                            onClick={async () => {
                              if (await act(() => api("POST", `/api/intake/tickets/${t.id}/escalate`, { raisePriority: true }), "Escalated.")) reload();
                            }}
                          >
                            Escalate
                          </Button>
                        )}
                        {!closed && ctx.projectId !== null && (
                          <Button
                            size="small"
                            icon={<ArrowRight16Regular />}
                            onClick={async () => {
                              if (await act(() => api("POST", `/api/intake/tickets/${t.id}/to-task`, { projectId: ctx.projectId }), "Task created from the ticket.")) reload();
                            }}
                          >
                            To task
                          </Button>
                        )}
                        {closed && (
                          <Button
                            size="small"
                            onClick={async () => {
                              if (await act(() => api("POST", `/api/intake/tickets/${t.id}/reopen`), "Reopened.")) reload();
                            }}
                          >
                            Reopen
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Page>

      <FormDialog
        open={newOpen}
        title="New ticket"
        action="Create ticket"
        disabled={!f.title.trim()}
        onClose={() => setNewOpen(false)}
        onSubmit={async () => {
          const ok = await act(
            () =>
              crud.create(R.ticket, {
                workspaceId: ctx.workspaceId,
                title: f.title.trim(),
                description: f.description.trim() || null,
                ticketType: f.type,
                priority: f.priority,
                requesterUserId: ctx.me.id,
                requesterEmail: ctx.me.email ?? null,
              }),
            "Ticket created.",
          );
          if (ok) {
            setNewOpen(false);
            setF({ title: "", description: "", type: "INCIDENT", priority: "MEDIUM" });
            reload();
          }
        }}
      >
        <Field label="Title" required>
          <Input value={f.title} onChange={(_, d) => setF({ ...f, title: d.value })} />
        </Field>
        <Field label="Details">
          <Textarea value={f.description} onChange={(_, d) => setF({ ...f, description: d.value })} rows={3} resize="vertical" />
        </Field>
        <div className={styles.formRow}>
          <Field label="Type" className={styles.grow}>
            <Select value={f.type} onChange={(_, d) => setF({ ...f, type: d.value })}>
              {["INCIDENT", "REQUEST", "PROBLEM", "CHANGE"].map((x) => (
                <option key={x} value={x}>
                  {nice(x)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Priority" className={styles.grow}>
            <Select value={f.priority} onChange={(_, d) => setF({ ...f, priority: d.value })}>
              {["LOW", "MEDIUM", "HIGH", "CRITICAL"].map((x) => (
                <option key={x} value={x}>
                  {nice(x)}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </FormDialog>

      <FormDialog
        open={resolveFor !== null}
        title={resolveFor ? `Resolve #${resolveFor.ticketNumber}` : "Resolve"}
        action="Resolve"
        onClose={() => setResolveFor(null)}
        onSubmit={async () => {
          if (!resolveFor) return;
          if (await act(() => api("POST", `/api/intake/tickets/${resolveFor.id}/resolve`, { resolution: resolution.trim() || null, createArticle: false }), "Ticket resolved.")) {
            setResolveFor(null);
            reload();
          }
        }}
      >
        <Field label="What fixed it?">
          <Textarea value={resolution} onChange={(_, d) => setResolution(d.value)} rows={3} resize="vertical" />
        </Field>
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Integrations
 * =================================================================== */
interface Hook {
  id: number;
  url: string;
  eventsJson: string;
  isActive: boolean;
  lastStatus?: number | null;
  failureCount: number;
}
interface Delivery {
  id: number;
  eventType: string;
  statusCode?: number | null;
  attempts: number;
  deliveredAt?: string | null;
}

export const IntegrationsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [events, setEvents] = useState<string[]>(["task.created"]);
  const [logFor, setLogFor] = useState<Hook | null>(null);
  const feed = useLoad(() => api<{ url: string }>("GET", "/api/integrations/calendar/feed-url"), []);
  const catalog = useLoad(async () => asList<string>(await api("GET", "/api/integrations/events")), []);
  const hooks = useLoad(async () => (await crud.list<Hook>(R.webhook, { workspaceId: ctx.workspaceId })).items, [ctx.workspaceId]);
  const log = useLoad(async () => (logFor ? asList<Delivery>(await api("GET", `/api/integrations/webhooks/${logFor.id}/deliveries`)) : []), [logFor?.id]);
  const list = hooks.data ?? [];
  const evNames = (h: Hook) => {
    try {
      const v = JSON.parse(h.eventsJson) as unknown;
      return Array.isArray(v) ? v.map(String).join(", ") : String(h.eventsJson);
    } catch {
      return h.eventsJson;
    }
  };

  return (
    <>
      <Head icon={<PlugConnected20Regular />} title="Integrations" sub="Calendar feed, webhooks and connections to other tools.">
        <Button className={styles.primary} appearance="primary" icon={<Add20Regular />} onClick={() => setOpen(true)}>
          New webhook
        </Button>
      </Head>
      <Page>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Calendar feed for Outlook and Google Calendar</h3>
          <p className={px.text}>
            Add this private link as a calendar subscription and your tasks with a due date show up next to your meetings. Anyone with the link can see your tasks, so don’t share it.
          </p>
          <Wait loading={feed.loading && !feed.data} error={feed.error} />
          {feed.data && (
            <div className={px.formLine}>
              <Input className={styles.grow} readOnly value={feed.data.url} aria-label="Calendar feed link" onFocus={(e) => e.currentTarget.select()} />
              <Button
                icon={<Copy20Regular />}
                onClick={() => {
                  void navigator.clipboard
                    .writeText(feed.data?.url ?? "")
                    .then(() => ctx.notify("success", "Link copied."))
                    .catch(() => ctx.notify("info", "Select the link and copy it."));
                }}
              >
                Copy
              </Button>
            </div>
          )}
        </div>

        <div className={px.card}>
          <h3 className={px.cardTitle}>Webhooks</h3>
          <Wait loading={hooks.loading && !hooks.data} error={hooks.error} empty={!hooks.loading && list.length === 0 && "No webhooks yet"} onRetry={hooks.reload} />
          {list.map((h) => (
            <div key={h.id} className={px.item}>
              <div className={px.itemMain}>
                <span className={px.itemTitle}>{h.url}</span>
                <span className={px.itemMeta}>
                  {evNames(h)} · last status {h.lastStatus ?? "—"}
                  {h.failureCount > 0 ? ` · ${h.failureCount} failures` : ""}
                </span>
              </div>
              <Switch
                checked={h.isActive}
                aria-label={`${h.url} is ${h.isActive ? "on" : "off"}`}
                onChange={async (_, o) => {
                  if (await act(() => crud.update(R.webhook, h.id, { isActive: o.checked }))) hooks.reload();
                }}
              />
              <Button
                size="small"
                onClick={async () => {
                  if (await act(() => api("POST", `/api/integrations/webhooks/${h.id}/test`), "Test sent.")) hooks.reload();
                }}
              >
                Test
              </Button>
              <Button size="small" onClick={() => setLogFor(h)}>
                Deliveries
              </Button>
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label="Delete webhook"
                onClick={async () => {
                  if (await act(() => crud.remove(R.webhook, h.id), "Webhook deleted.")) hooks.reload();
                }}
              />
            </div>
          ))}
        </div>
      </Page>

      <FormDialog
        open={open}
        title="New webhook"
        action="Create webhook"
        disabled={!/^https?:\/\//i.test(url.trim()) || events.length === 0}
        onClose={() => setOpen(false)}
        onSubmit={async () => {
          const ok = await act(
            () => crud.create(R.webhook, { workspaceId: ctx.workspaceId, url: url.trim(), eventsJson: JSON.stringify(events), secret: secret.trim() || null }),
            "Webhook created.",
          );
          if (ok) {
            setOpen(false);
            setUrl("");
            setSecret("");
            hooks.reload();
          }
        }}
      >
        <Field label="Address" required hint="We send a POST here each time one of the events happens.">
          <Input value={url} onChange={(_, d) => setUrl(d.value)} placeholder="https://example.com/hooks/tasks" />
        </Field>
        <Field label="Secret" hint="Optional. Used to sign each request.">
          <Input value={secret} onChange={(_, d) => setSecret(d.value)} />
        </Field>
        <Field label="Events">
          <div className={px.checks}>
            {(catalog.data ?? []).map((e) => (
              <Checkbox key={e} label={e} checked={events.includes(e)} onChange={(_, d) => setEvents(d.checked === true ? [...events, e] : events.filter((x) => x !== e))} />
            ))}
          </div>
        </Field>
      </FormDialog>

      <Dialog open={logFor !== null} onOpenChange={(_, o) => !o.open && setLogFor(null)}>
        <DialogSurface className={styles.dialog}>
          <DialogBody>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} />
                </DialogTrigger>
              }
            >
              Recent deliveries
            </DialogTitle>
            <DialogContent className={styles.formBody}>
              <Wait loading={log.loading} error={log.error} empty={!log.loading && (log.data ?? []).length === 0 && "Nothing sent yet"} />
              {(log.data ?? []).slice(0, 20).map((d) => (
                <div key={d.id} className={px.item}>
                  <div className={px.itemMain}>
                    <span className={px.itemTitle}>{d.eventType}</span>
                    <span className={px.itemMeta}>
                      {fmtStamp(d.deliveredAt)} · {d.attempts} {d.attempts === 1 ? "attempt" : "attempts"}
                    </span>
                  </div>
                  <Chip text={d.statusCode && d.statusCode < 300 ? "OK" : `Error ${d.statusCode ?? ""}`.trim()} />
                </div>
              ))}
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

/* =================================================================== *
 *  Automation
 * =================================================================== */
interface RuleRow {
  id: number;
  projectId?: number | null;
  name: string;
  triggerType: string;
  triggerConfigJson?: string | null;
  actionsJson: string;
  isActive: boolean;
  runCount: number;
  lastRunAt?: string | null;
}
interface StatusRow {
  id: number;
  projectId?: number | null;
  name: string;
}

const TRIGGERS: { key: string; label: string; needsStatus?: boolean }[] = [
  { key: "TASK_CREATED", label: "Task is created" },
  { key: "STATUS_CHANGED", label: "Task moves to a column", needsStatus: true },
  { key: "TASK_COMPLETED", label: "Task is completed" },
  { key: "DUE_IN_DAYS", label: "Task is due in 24 hours" },
  { key: "OVERDUE", label: "Task becomes overdue" },
  { key: "PRIORITY_CHANGED", label: "Priority is set to High" },
  { key: "COMMENT_ADDED", label: "Someone comments" },
  { key: "ASSIGNEE_ADDED", label: "Someone is assigned" },
];
const ACTIONS: { key: string; label: string; needsStatus?: boolean; needsText?: boolean }[] = [
  { key: "notify_assignees", label: "Notify assignees" },
  { key: "notify_owner", label: "Notify project owner" },
  { key: "set_priority", label: "Raise priority to High" },
  { key: "set_status", label: "Move task to a column", needsStatus: true },
  { key: "comment", label: "Add a comment", needsText: true },
];

const triggerConfig = (key: string, statusId: number | null): Record<string, unknown> => {
  if (key === "STATUS_CHANGED") return { statusId };
  if (key === "DUE_IN_DAYS") return { days: 1 };
  if (key === "PRIORITY_CHANGED") return { priority: "HIGH" };
  return {};
};
const actionJson = (key: string, name: string, statusId: number | null, text: string): Record<string, unknown> => {
  if (key === "notify_assignees") return { type: "notify", users: ["assignees"], event: "RULE_NOTICE", title: `${name}: {task.title}` };
  if (key === "notify_owner") return { type: "notify", users: ["project_owner"], event: "RULE_NOTICE", title: `${name}: {task.title}` };
  if (key === "set_priority") return { type: "set_priority", value: "HIGH" };
  if (key === "set_status") return { type: "set_status", statusId };
  return { type: "comment", text };
};

export const AutomationPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [project, setProject] = useState("");
  const [trigger, setTrigger] = useState("TASK_CREATED");
  const [trigStatus, setTrigStatus] = useState("");
  const [action, setAction] = useState("notify_assignees");
  const [actStatus, setActStatus] = useState("");
  const [text, setText] = useState("");
  const rules = useLoad(async () => (await crud.list<RuleRow>(R.rule, { workspaceId: ctx.workspaceId, sort: "-id" })).items, [ctx.workspaceId]);
  const statuses = useLoad(async () => (await crud.list<StatusRow>(R.status, { workspaceId: ctx.workspaceId, pageSize: 500 })).items, [ctx.workspaceId]);
  const list = rules.data ?? [];
  const statusName = (id: unknown) => statuses.data?.find((s) => s.id === Number(id))?.name ?? "a column";
  const projStatuses = (statuses.data ?? []).filter((s) => String(s.projectId ?? "") === project);
  const trig = TRIGGERS.find((t) => t.key === trigger);
  const act1 = ACTIONS.find((a) => a.key === action);

  const describeTrigger = (r: RuleRow) => {
    let cfg: Record<string, unknown> = {};
    try {
      cfg = r.triggerConfigJson ? (JSON.parse(r.triggerConfigJson) as Record<string, unknown>) : {};
    } catch {
      /* ignore */
    }
    if (r.triggerType === "STATUS_CHANGED") return `Task moves to ${statusName(cfg.statusId)}`;
    return TRIGGERS.find((t) => t.key === r.triggerType)?.label ?? nice(r.triggerType);
  };
  const describeActions = (r: RuleRow) => {
    try {
      const arr = JSON.parse(r.actionsJson) as { type?: string; users?: string[]; statusId?: unknown }[];
      return (Array.isArray(arr) ? arr : [arr])
        .map((a) => {
          if (a.type === "notify") return a.users?.includes("project_owner") ? "Notify project owner" : "Notify assignees";
          if (a.type === "set_priority") return "Raise priority to High";
          if (a.type === "set_status") return `Move task to ${statusName(a.statusId)}`;
          if (a.type === "comment") return "Add a comment";
          return nice(String(a.type ?? "action"));
        })
        .join(", ");
    } catch {
      return "Custom actions";
    }
  };

  const shown = list.filter((r) => `${r.name} ${describeTrigger(r)} ${describeActions(r)}`.toLowerCase().includes(query.trim().toLowerCase()));
  const activeCount = list.filter((r) => r.isActive).length;
  const needProject = !!trig?.needsStatus || !!act1?.needsStatus;
  const valid = name.trim() && (!needProject || project) && (!trig?.needsStatus || trigStatus) && (!act1?.needsStatus || actStatus) && (!act1?.needsText || text.trim());

  const create = async () => {
    const ok = await act(
      () =>
        crud.create(R.rule, {
          workspaceId: ctx.workspaceId,
          projectId: project ? Number(project) : null,
          name: name.trim(),
          triggerType: trigger,
          triggerConfigJson: JSON.stringify(triggerConfig(trigger, trigStatus ? Number(trigStatus) : null)),
          actionsJson: JSON.stringify([actionJson(action, name.trim(), actStatus ? Number(actStatus) : null, text.trim())]),
          isActive: true,
        }),
      "Automation created.",
    );
    if (ok) {
      setOpen(false);
      rules.reload();
    }
  };

  return (
    <>
      <Head icon={<Flash20Regular />} title="Automation" sub="Set rules so routine task updates happen on their own." />
      <div className={styles.bar}>
        <span className={styles.autoCount}>
          {activeCount} of {list.length} automations active
        </span>
        <div className={styles.tools}>
          <SearchBox className={styles.search} placeholder="Search" value={query} onChange={(_, d) => setQuery(d.value)} />
          <Button
            className={styles.primary}
            appearance="primary"
            icon={<Add20Regular />}
            onClick={() => {
              setName("");
              setProject(ctx.projectId === null ? "" : String(ctx.projectId));
              setTrigger("TASK_CREATED");
              setTrigStatus("");
              setAction("notify_assignees");
              setActStatus("");
              setText("");
              setOpen(true);
            }}
          >
            Create Automation
          </Button>
        </div>
      </div>
      <div className={styles.body}>
        <Wait loading={rules.loading && !rules.data} error={rules.error} onRetry={rules.reload} />
        <div className={styles.autoGrid}>
          {shown.map((r) => (
            <section key={r.id} className={`${styles.autoCard} ${r.isActive ? "" : styles.autoOff}`}>
              <div className={styles.autoTop}>
                <h3 className={styles.autoName}>{r.name}</h3>
                <Switch
                  checked={r.isActive}
                  aria-label={`${r.name} is ${r.isActive ? "on" : "off"}`}
                  onChange={async (_, d) => {
                    if (await act(() => crud.update(R.rule, r.id, { isActive: d.checked }))) rules.reload();
                  }}
                />
              </div>
              <div className={styles.autoFlow}>
                <div className={styles.autoStep}>
                  <span className={styles.autoLabel}>When</span>
                  <span className={styles.autoChip}>{describeTrigger(r)}</span>
                </div>
                <ArrowRight16Regular className={styles.autoArrow ?? ""} />
                <div className={styles.autoStep}>
                  <span className={styles.autoLabel}>Then</span>
                  <span className={styles.autoChip}>{describeActions(r)}</span>
                </div>
              </div>
              <div className={styles.autoFoot}>
                <span>
                  {r.runCount} runs · {r.lastRunAt ? fmtStamp(r.lastRunAt) : "Never run"}
                </span>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<Delete20Regular />}
                  aria-label={`Delete ${r.name}`}
                  onClick={async () => {
                    if (await act(() => crud.remove(R.rule, r.id), "Automation deleted.")) rules.reload();
                  }}
                />
              </div>
            </section>
          ))}
          {!rules.loading && shown.length === 0 && <div className={styles.emptyRow}>No automations found</div>}
        </div>
      </div>

      <FormDialog open={open} title="New Automation" action="Create Automation" disabled={!valid} onClose={() => setOpen(false)} onSubmit={() => void create()}>
        <Field label="Name" required>
          <Input value={name} onChange={(_, d) => setName(d.value)} placeholder="Remind assignees before due date" />
        </Field>
        <Field label="Project" hint={needProject ? "Required for rules that use a column." : "Leave empty to apply to every project."}>
          <Select value={project} onChange={(_, d) => (setProject(d.value), setTrigStatus(""), setActStatus(""))}>
            <option value="">All projects</option>
            {ctx.projects.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="When this happens">
          <Select value={trigger} onChange={(_, d) => setTrigger(d.value)}>
            {TRIGGERS.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </Select>
        </Field>
        {trig?.needsStatus && (
          <Field label="Column">
            <Select value={trigStatus} onChange={(_, d) => setTrigStatus(d.value)}>
              <option value="">{project ? "Choose a column" : "Choose a project first"}</option>
              {projStatuses.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Do this">
          <Select value={action} onChange={(_, d) => setAction(d.value)}>
            {ACTIONS.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </Select>
        </Field>
        {act1?.needsStatus && (
          <Field label="Move to column">
            <Select value={actStatus} onChange={(_, d) => setActStatus(d.value)}>
              <option value="">{project ? "Choose a column" : "Choose a project first"}</option>
              {projStatuses.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {act1?.needsText && (
          <Field label="Comment">
            <Textarea value={text} onChange={(_, d) => setText(d.value)} rows={2} resize="vertical" />
          </Field>
        )}
      </FormDialog>
    </>
  );
};

/* =================================================================== *
 *  Settings
 * =================================================================== */
interface PrefEvent {
  eventType: string;
  inApp: boolean;
  email: boolean;
  push: boolean;
}
interface NotifPrefs {
  digestMode: string;
  quietStart?: string | null;
  quietEnd?: string | null;
  events: PrefEvent[];
}

export const SettingsPage: React.FC<{ ctx: PageCtx }> = ({ ctx }) => {
  const act = useAct(ctx.notify);
  const { me } = ctx;
  const [tz, setTz] = useState(me.timeZone);
  const [cur, setCur] = useState(me.currencyCode ?? "");
  const [ws, setWs] = useState(String(me.defaultWorkspaceId ?? ctx.workspaceId));
  const [dow, setDow] = useState("1");
  const prefs = useLoad(() => api<NotifPrefs>("GET", "/api/notifications/preferences"), []);
  const zones = useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? [];
    } catch {
      return [];
    }
  }, []);

  const savePrefs = async () => {
    await act(
      () =>
        api("PUT", "/api/identity/me/preferences", {
          timeZone: tz.trim() || null,
          currencyCode: cur.trim() || null,
          defaultWorkspaceId: Number(ws),
          firstDayOfWeek: Number(dow),
        }),
      "Saved.",
    );
  };
  const setChannel = async (eventType: string, channel: "INAPP" | "EMAIL" | "PUSH", isEnabled: boolean) => {
    prefs.setData((p) =>
      p
        ? {
            ...p,
            events: p.events.map((e) => (e.eventType === eventType ? { ...e, ...(channel === "INAPP" ? { inApp: isEnabled } : channel === "EMAIL" ? { email: isEnabled } : { push: isEnabled }) } : e)),
          }
        : p,
    );
    if (!(await act(() => api("PUT", "/api/notifications/preferences", { items: [{ eventType, channel, isEnabled }] })))) prefs.reload();
  };

  return (
    <>
      <Head icon={<Settings20Regular />} title="Settings" sub="Your account, preferences and notifications." />
      <Page>
        <div className={px.card}>
          <h3 className={px.cardTitle}>Account</h3>
          <p className={px.text}>
            {me.displayName || "You"} · {me.email ?? "no email"} · {me.permissions.length} permissions
          </p>
        </div>

        <div className={px.card}>
          <h3 className={px.cardTitle}>Preferences</h3>
          <div className={px.formGrid}>
            <Field label="Time zone">
              <Input list="tm-zones" value={tz} onChange={(_, d) => setTz(d.value)} />
            </Field>
            <datalist id="tm-zones">
              {zones.map((z) => (
                <option key={z} value={z} />
              ))}
            </datalist>
            <Field label="Currency (3 letters)">
              <Input value={cur} maxLength={3} onChange={(_, d) => setCur(d.value.toUpperCase())} />
            </Field>
            <Field label="Opens with workspace">
              <Select value={ws} onChange={(_, d) => setWs(d.value)}>
                {me.workspaces.map((w) => (
                  <option key={w.id} value={String(w.id)}>
                    {w.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Week starts on">
              <Select value={dow} onChange={(_, d) => setDow(d.value)}>
                <option value="1">Monday</option>
                <option value="0">Sunday</option>
                <option value="6">Saturday</option>
              </Select>
            </Field>
          </div>
          <div className={px.actions}>
            <Button appearance="primary" className={styles.primary} icon={<Checkmark20Regular />} onClick={() => void savePrefs()}>
              Save preferences
            </Button>
          </div>
        </div>

        <div className={px.card}>
          <h3 className={px.cardTitle}>Notifications</h3>
          <Wait loading={prefs.loading && !prefs.data} error={prefs.error} onRetry={prefs.reload} />
          {prefs.data && (
            <>
              <div className={px.formLine}>
                <Field label="E-mail digest">
                  <Select
                    value={prefs.data.digestMode}
                    onChange={async (_, d) => {
                      if (await act(() => api("PUT", "/api/notifications/preferences", { digestMode: d.value, quietStart: prefs.data?.quietStart ?? null, quietEnd: prefs.data?.quietEnd ?? null }), "Saved.")) prefs.reload();
                    }}
                  >
                    <option value="NONE">Off</option>
                    <option value="HOURLY">Hourly</option>
                    <option value="DAILY">Daily</option>
                  </Select>
                </Field>
              </div>
              <table className={px.tbl}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>In the app</th>
                    <th>E-mail</th>
                    <th>Push</th>
                  </tr>
                </thead>
                <tbody>
                  {prefs.data.events.map((e) => (
                    <tr key={e.eventType}>
                      <td>{nice(e.eventType)}</td>
                      <td>
                        <Switch checked={e.inApp} aria-label={`${nice(e.eventType)} in the app`} onChange={(_, d) => void setChannel(e.eventType, "INAPP", d.checked)} />
                      </td>
                      <td>
                        <Switch checked={e.email} aria-label={`${nice(e.eventType)} by e-mail`} onChange={(_, d) => void setChannel(e.eventType, "EMAIL", d.checked)} />
                      </td>
                      <td>
                        <Switch checked={e.push} aria-label={`${nice(e.eventType)} push`} onChange={(_, d) => void setChannel(e.eventType, "PUSH", d.checked)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </Page>
    </>
  );
};

/* shared building blocks for the other page files */
export { Head, Page, Wait, Tile, FormDialog, Chip, Prio, useLoad, useAct, downloadCsv, fmtDate, fmtStamp, fmtMin, asList, nice, mondayOf };